/**
 * §P.7 fase 2 · Los interruptores del club.
 *
 * ## Cuatro van de comodidad y uno va de daño
 *
 * `weight_logs_under_16` no es una preferencia: **en deportes con categorías de peso
 * hay un riesgo real de trastornos alimentarios** en menores, y `brainstorming.md`
 * §14 lo tiene en la lista de lo que no se construye para menores. Está apagado de
 * fábrica, el bloqueo es del servidor —`422`, no un botón escondido— y encenderlo es
 * una decisión consciente del club.
 *
 * Por eso aquí va **aparte de los otros cuatro**, con su propio título y con la
 * consecuencia escrita antes de la casilla. Mezclarlo en una lista de cinco
 * casillas junto a «felicitar cumpleaños» es exactamente cómo alguien lo enciende sin
 * leerlo.
 *
 * ## Se guarda con `PUT`, así que la lista viaja entera
 *
 * Como en las disciplinas: lo que no se manda se pierde. Se manda el objeto completo
 * siempre, no lo que se ha tocado.
 *
 * ## Ningún interruptor de aquí decide cuánto se cobra
 *
 * `academies.settings` es un `jsonb` y `DDBB.md` §10 lo define como *preferencias de
 * interfaz, no reglas de cobro* (H-5). Un importe guardado en un `jsonb` sin validar
 * es un cobro equivocado esperando su turno, así que eso son columnas con tipo y con
 * `CHECK`. Si algún día alguien quiere añadir aquí un «cobrar matrícula», la
 * respuesta está escrita.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import type { AjustesDelClub } from '@/club/ajustes/tipos';

/** Los cuatro de comodidad, con lo que significan de verdad. */
const INTERRUPTORES: readonly {
  clave: 'internal_ranking' | 'birthdays' | 'second_hand';
  etiqueta: string;
  explicacion: string;
}[] = [
  {
    clave: 'birthdays',
    etiqueta: 'Felicitar los cumpleaños',
    explicacion:
      'Sale en el muro de la app el día que toca. Es de las cosas que más se agradecen y la que menos trabajo da.',
  },
  {
    clave: 'internal_ranking',
    etiqueta: 'Tabla de asistencia entre alumnos',
    explicacion:
      'Las familias ven quién ha venido más. Motiva a algunos y desanima a otros: en grupos infantiles conviene pensarlo.',
  },
  {
    clave: 'second_hand',
    etiqueta: 'Tablón de segunda mano',
    explicacion:
      'Las familias se pasan dobok y protecciones entre ellas. Ahorra dinero a los que empiezan y no te da trabajo.',
  },
];

const RECORDATORIOS: readonly {
  clave: 'session' | 'exam' | 'payment';
  etiqueta: string;
  explicacion: string;
}[] = [
  {
    clave: 'session',
    etiqueta: 'Antes de cada clase',
    explicacion:
      'Todas las semanas y a todas las familias. Es el que más rápido se convierte en ruido que nadie mira.',
  },
  {
    clave: 'exam',
    etiqueta: 'Antes de un examen de grado',
    explicacion: 'Pocas veces al año y justo cuando importa.',
  },
  {
    clave: 'payment',
    etiqueta: 'Cuando hay un recibo pendiente',
    explicacion:
      'Ahorra la conversación más incómoda del mes, y la ahorra sin que nadie tenga que sacar el tema.',
  },
];

export function Interruptores({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [ajustes, setAjustes] = useState<AjustesDelClub | null>(null);
  const [sembradoDe, setSembradoDe] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [guardado, setGuardado] = useState(false);

  const actuales = useQuery({
    queryKey: ['ajustes', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).settings.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as AjustesDelClub;
    },
  });

  if (actuales.data && sembradoDe !== club) {
    setSembradoDe(club);
    setAjustes(actuales.data);
  }

  const guardar = useMutation({
    mutationFn: async (a: AjustesDelClub) => {
      // El objeto entero: es un `PUT`, y lo que no se manda se pierde.
      const r = await api.v1.clubs({ club }).settings.put({
        internal_ranking: a.internal_ranking,
        birthdays: a.birthdays,
        second_hand: a.second_hand,
        reminders: { ...a.reminders },
        weight_logs_under_16: a.weight_logs_under_16,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setGuardado(true);
      void cache.invalidateQueries({ queryKey: ['ajustes', club] });
    },
    onError: (e) => {
      setGuardado(false);
      setProblema(aProblema(e));
    },
  });

  if (actuales.isPending || !ajustes) {
    return <Cargando filas={6} etiqueta="Cargando los ajustes" />;
  }

  if (actuales.isError || !actuales.data) {
    return (
      <PanelError
        problema={aProblema(actuales.error)}
        reintentar={() => void actuales.refetch()}
      />
    );
  }

  const a = ajustes;
  const poner = <K extends keyof AjustesDelClub>(
    clave: K,
    valor: AjustesDelClub[K]
  ) => {
    setAjustes((x) => (x ? { ...x, [clave]: valor } : x));
    setGuardado(false);
  };
  const ponerRecordatorio = (
    clave: 'session' | 'exam' | 'payment',
    v: boolean
  ) => {
    setAjustes((x) =>
      x ? { ...x, reminders: { ...x.reminders, [clave]: v } } : x
    );
    setGuardado(false);
  };

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        guardar.mutate(a);
      }}
    >
      <h1>Ajustes</h1>

      <p className="tenue">
        Qué enseña Kiap a las familias y de qué les avisa. Nada de aquí cambia
        lo que se cobra: eso está en Cobros.
      </p>

      <h2 className="seccion">Qué ven las familias</h2>

      <ul className="roles">
        {INTERRUPTORES.map((i) => (
          <li key={i.clave}>
            <label className="opcion">
              <input
                type="checkbox"
                checked={a[i.clave]}
                disabled={!puedeEditar}
                onChange={(e) => poner(i.clave, e.target.checked)}
              />
              <span>
                <strong>{i.etiqueta}</strong>
                <em>{i.explicacion}</em>
              </span>
            </label>
          </li>
        ))}
      </ul>

      <h2 className="seccion">De qué avisamos</h2>

      <ul className="roles">
        {RECORDATORIOS.map((r) => (
          <li key={r.clave}>
            <label className="opcion">
              <input
                type="checkbox"
                checked={a.reminders[r.clave]}
                disabled={!puedeEditar}
                onChange={(e) => ponerRecordatorio(r.clave, e.target.checked)}
              />
              <span>
                <strong>{r.etiqueta}</strong>
                <em>{r.explicacion}</em>
              </span>
            </label>
          </li>
        ))}
      </ul>

      {/*
        Aparte, y con la consecuencia delante de la casilla. Ver la cabecera: este es
        el único de la lista que no va de comodidad.
      */}
      <h2 className="seccion">El peso de los menores de 16</h2>

      <div className="aviso" role="status">
        <p>
          <strong>
            En los deportes con categorías de peso hay un riesgo real de
            trastornos alimentarios.
          </strong>{' '}
          Por eso Kiap no deja anotar el peso de un menor de 16 años, y el
          bloqueo es del servidor: no es un botón escondido.
        </p>
        <p>
          Se puede abrir si tu club compite y lo necesita para inscribir en
          categoría. Es una decisión del club, no una opción más.
        </p>
      </div>

      <label className="opcion">
        <input
          type="checkbox"
          checked={a.weight_logs_under_16}
          disabled={!puedeEditar}
          onChange={(e) => poner('weight_logs_under_16', e.target.checked)}
        />
        <span>
          <strong>Permitir anotar el peso de menores de 16 años</strong>
          <em>
            Lo verán el dueño, el administrativo y el entrenador. Nunca la
            familia ni el propio alumno.
          </em>
        </span>
      </label>

      {problema && <PanelError problema={problema} />}

      {guardado && !guardar.isPending && (
        <div className="aviso aviso-calma" role="status">
          <strong>Guardado.</strong>
        </div>
      )}

      {puedeEditar && (
        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar los ajustes'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setAjustes(actuales.data);
              setProblema(null);
              setGuardado(false);
            }}
          >
            Deshacer los cambios
          </button>
        </div>
      )}
    </form>
  );
}
